'use client';

import { useQuery } from '@tanstack/react-query';
import { formatPrice, formatPercent, getChangeColor } from '@/lib/terminal-format';
import { TrendingUp, TrendingDown } from 'lucide-react';

export function MarketMovers() {
  const { data, isLoading } = useQuery<{ gainers: { symbol: string; price: number; changePercent: number }[]; losers: { symbol: string; price: number; changePercent: number }[] }>({
    queryKey: ['/api/terminal/market-movers'],
    queryFn: async () => {
      const res = await fetch('/api/terminal/market-movers');
      if (!res.ok) throw new Error('Failed');
      return res.json();
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-5 w-full bg-[#1F2937] animate-pulse rounded" />
        ))}
      </div>
    );
  }

  const { gainers = [], losers = [] } = data || {};

  return (
    <div className="grid grid-cols-2 gap-0 divide-x divide-[#1F2937]">
      <div>
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[#1F2937]">
          <TrendingUp className="w-3 h-3 text-terminal-positive" />
          <span className="text-[10px] font-semibold text-terminal-positive uppercase tracking-wider">Gainers</span>
        </div>
        <div className="divide-y divide-[#1F2937]/50">
          {gainers.map((item, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-[#1A2035] transition-colors">
              <span className="font-mono text-xs font-medium text-[#E5E7EB]">{item.symbol}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs tabular-nums text-[#E5E7EB]">{formatPrice(item.price)}</span>
                <span className="font-mono text-[10px] tabular-nums text-terminal-positive min-w-[52px] text-right">
                  {formatPercent(item.changePercent)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-[#1F2937]">
          <TrendingDown className="w-3 h-3 text-terminal-negative" />
          <span className="text-[10px] font-semibold text-terminal-negative uppercase tracking-wider">Losers</span>
        </div>
        <div className="divide-y divide-[#1F2937]/50">
          {losers.map((item, i) => (
            <div key={i} className="flex items-center justify-between px-2 py-1.5 hover:bg-[#1A2035] transition-colors">
              <span className="font-mono text-xs font-medium text-[#E5E7EB]">{item.symbol}</span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs tabular-nums text-[#E5E7EB]">{formatPrice(item.price)}</span>
                <span className="font-mono text-[10px] tabular-nums text-terminal-negative min-w-[52px] text-right">
                  {formatPercent(item.changePercent)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
